import React from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

type SelectableUnit = {
  id: string;
  name: string;
  isActive?: boolean;
};

type ResultUnitsSectionProps = {
  isLoadingData: boolean;
  loadError?: string | null;
  selectableUnits: SelectableUnit[];
  selectedUnits: Set<string>;
  isAllSelected: boolean;
  onToggleAllUnits: () => void;
  onUnitToggle: (unitId: string) => void;
};

const ResultUnitsSection: React.FC<ResultUnitsSectionProps> = ({
  isLoadingData,
  loadError,
  selectableUnits,
  selectedUnits,
  isAllSelected,
  onToggleAllUnits,
  onUnitToggle,
}) => {
  const theme = useTheme();

  if (!isLoadingData && selectableUnits.length === 0 && !loadError) {
    return null;
  }

  const resultUnitCheckboxSx = {
    borderRadius: 1,
    color: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.7)
      : alpha(theme.palette.text.primary, 0.54),
    '&.Mui-checked': {
      color: theme.palette.mode === 'dark'
        ? theme.palette.common.white
        : theme.palette.primary.main,
    },
  };

  const resultUnitItemSx = (selected: boolean, disabled?: boolean) => ({
    p: 1.5,
    m: 0,
    borderRadius: '8px !important',
    border: `1px solid ${selected
      ? theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.light, 0.5)
        : alpha(theme.palette.primary.main, 0.24)
      : alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.18 : 0.38)}`,
    backgroundColor: selected
      ? theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.light, 0.18)
        : alpha(theme.palette.primary.main, 0.08)
      : 'transparent',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: disabled
        ? 'transparent'
        : selected
          ? theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.light, 0.2)
            : alpha(theme.palette.primary.main, 0.1)
          : alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.04 : 0.025),
    },
    opacity: disabled ? 0.6 : 1,
  });

  return (
    <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 2, background: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : alpha(theme.palette.background.paper, 0.98), boxShadow: theme.palette.mode === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.12)' : '0 1px 2px rgba(0, 0, 0, 0.05)' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 0, fontWeight: 650, letterSpacing: '-0.01em' }}>
            Resultatenheter
          </Typography>
        </Box>
        {!isLoadingData && selectableUnits.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            onClick={onToggleAllUnits}
            sx={{
              textTransform: 'none',
              borderColor: alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.28 : 0.48),
              color: theme.palette.text.primary,
              backgroundColor: 'transparent',
            }}
          >
            {isAllSelected ? 'Avmarkera alla' : 'Markera alla'}
          </Button>
        )}
      </Stack>

      {isLoadingData ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          {Array.from({ length: 16 }).map((_, idx) => (
            <Skeleton key={idx} variant="rounded" height={64} sx={{ borderRadius: 1.5 }} />
          ))}
        </Box>
      ) : loadError ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {loadError}
        </Alert>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          {selectableUnits.map((unit) => (
            <FormControlLabel
              key={unit.id}
              control={
                <Checkbox
                  checked={selectedUnits.has(unit.id)}
                  onChange={() => onUnitToggle(unit.id)}
                  disabled={!unit.isActive && unit.isActive !== undefined}
                  sx={resultUnitCheckboxSx}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {unit.name}
                  {unit.isActive === false && (
                    <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                      inaktiv
                    </Typography>
                  )}
                </Typography>
              }
              sx={resultUnitItemSx(selectedUnits.has(unit.id), !unit.isActive && unit.isActive !== undefined)}
            />
          ))}
        </Box>
      )}

      {isLoadingData ? (
        <Skeleton variant="text" width={120} height={20} sx={{ mt: 2 }} />
      ) : loadError ? null : (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
          Valda: {selectedUnits.size}/{selectableUnits.length}
        </Typography>
      )}
    </Paper>
  );
};

export default React.memo(ResultUnitsSection);
